import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsOperationsApi, type StockCardDto, type GoodsOperationResultDto } from '../../api/goodsOperations';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import MoneyCell from '../../components/MoneyCell';
import AccountSelect from '../../components/AccountSelect';

type OpKind = 'acquire' | 'discard' | 'transfer' | 'inventorize';

/**
 * 2a/2b — Prekių operacijos: the four standalone write documents (gavimas,
 * nurašymas, pervedimas, inventorizacija) with their JEs (Persistent/Periodic
 * per the goods item), plus the per-item stock card with running balance.
 * Mirror workspaces: read-only (ADR-004).
 */
export default function GoodsOperationsPage() {
  const isMirror = useIsMirror();
  const now = new Date().toISOString().slice(0, 10);
  const [kind, setKind] = useState<OpKind>('acquire');
  const [date, setDate] = useState(now);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GoodsOperationResultDto | null>(null);

  // Shared selects.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: goodsOperationsApi.warehouses,
    staleTime: 5 * 60 * 1000,
  });
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [toWarehouseId, setToWarehouseId] = useState<number | null>(null);
  const [counterAccount, setCounterAccount] = useState<number | null>(null);
  const [costAccount, setCostAccount] = useState<number | null>(null);

  // Goods rows (free entry by goods id; the mirror stock-summary table links here).
  const [lines, setLines] = useState<{ goodsId: string; quantity: string; unitCost: string }[]>([
    { goodsId: '', quantity: '', unitCost: '' },
  ]);
  const setLine = (i: number, patch: Partial<typeof lines[0]>) =>
    setLines(prev => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  // Stock card viewer.
  const [cardGoodsId, setCardGoodsId] = useState('');
  const [card, setCard] = useState<StockCardDto | null>(null);

  const submit = async () => {
    setError('');
    setResult(null);
    const parsed = lines
      .filter(l => l.goodsId && l.quantity)
      .map(l => ({ goodsId: Number(l.goodsId), quantity: Number(l.quantity), unitCost: Number(l.unitCost) || 0 }));
    if (parsed.length === 0) {
      setError('Įveskite bent vieną prekės eilutę (id + kiekis).');
      return;
    }
    if ((kind === 'acquire' || kind === 'discard') && !warehouseId) {
      setError('Pasirinkite sandėlį.');
      return;
    }
    if (kind === 'transfer' && (!warehouseId || !toWarehouseId)) {
      setError('Pervedimui reikia abiejų sandėlių.');
      return;
    }
    setBusy(true);
    try {
      const r =
        kind === 'acquire'
          ? await goodsOperationsApi.acquire({ date, warehouseId: warehouseId!, counterAccount, lines: parsed })
          : kind === 'discard'
            ? await goodsOperationsApi.discard({ date, warehouseId: warehouseId!, costAccount, lines: parsed })
            : kind === 'transfer'
              ? await goodsOperationsApi.transfer({ date, fromWarehouseId: warehouseId!, toWarehouseId: toWarehouseId!, lines: parsed })
              : await goodsOperationsApi.inventorize({
                  date,
                  lines: parsed.map(l => ({ ...l, warehouseId: warehouseId!, countedQuantity: l.quantity })),
                });
      setResult(r);
      setLines([{ goodsId: '', quantity: '', unitCost: '' }]);
    } catch (e: any) {
      setError(e.message || 'Nepavyko įrašyti operacijos');
    } finally {
      setBusy(false);
    }
  };

  const loadCard = async () => {
    if (!cardGoodsId) return;
    setError('');
    try {
      setCard(await goodsOperationsApi.stockCard({
        goodsId: Number(cardGoodsId),
        from: '2000-01-01',
        to: new Date().toISOString().slice(0, 10),
      }));
    } catch (e: any) {
      setError(e.message || 'Nepavyko gauti kortelės');
    }
  };

  useReportScreen(
    `Prekių operacijos (${kind})${result ? ` — ${result.documentNumber}` : ''}${isMirror ? ' (tik skaityti)' : ''}`,
    { recordCount: card?.rows.length ?? 0 },
  );

  const whSelect = (value: number | null, onChange: (v: number | null) => void, label: string) => (
    <label className="text-sm">
      <span className="block text-gray-500 mb-1">{label}</span>
      <select value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}>
        <option value="">— sandėlis —</option>
        {(warehouses ?? []).filter(w => !w.isObsolete).map(w => (
          <option key={w.id} value={w.id}>{w.name} ({w.warehouseAccount})</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Prekių operacijos</h1>
          <p className="text-sm text-gray-500">
            Gavimas, nurašymas, pervedimas, inventorizacija — su žurnalo įrašais (taisyklinga / periodinė apskaita).
          </p>
        </div>
        <Link
          to="/inventory"
          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
        >
          ← Atsargos
        </Link>
      </div>

      {isMirror && <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti — operacijas kurkite Apskaita5 programoje.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {result && (
        <p className="text-sm text-green-700">
          Įrašyta: {result.documentNumber} · {result.operationCount} eilutės · JE {result.journalEntryId || '—'}
        </p>
      )}

      {!isMirror && (
        <div className="card" style={{ padding: '1rem' }}>
          <div className="flex items-end gap-2 flex-wrap mb-3">
            <label className="text-sm">
              <span className="block text-gray-500 mb-1">Operacija</span>
              <select value={kind} onChange={e => setKind(e.target.value as OpKind)}>
                <option value="acquire">Gavimas</option>
                <option value="discard">Nurašymas</option>
                <option value="transfer">Pervedimas</option>
                <option value="inventorize">Inventorizacija</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-gray-500 mb-1">Data</span>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </label>
            {(kind === 'acquire' || kind === 'discard' || kind === 'inventorize') &&
              whSelect(warehouseId, setWarehouseId, 'Sandėlis')}
            {kind === 'transfer' && whSelect(toWarehouseId, setToWarehouseId, 'Į sandėlį')}
            {kind === 'acquire' && (
              <label className="text-sm">
                <span className="block text-gray-500 mb-1">Kontosąskaita</span>
                <AccountSelect value={counterAccount} onChange={setCounterAccount} />
              </label>
            )}
            {kind === 'discard' && (
              <label className="text-sm">
                <span className="block text-gray-500 mb-1">Savikainos sąskaita</span>
                <AccountSelect value={costAccount} onChange={setCostAccount} />
              </label>
            )}
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Prekės ID</th>
                <th>{kind === 'inventorize' ? 'Suskaičiuota' : 'Kiekis'}</th>
                <th>Savikaina / vnt.</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <input style={{ width: '6rem' }} value={l.goodsId}
                      onChange={e => setLine(i, { goodsId: e.target.value })} placeholder="id" />
                  </td>
                  <td>
                    <input style={{ width: '6rem' }} value={l.quantity}
                      onChange={e => setLine(i, { quantity: e.target.value })} placeholder="kiekis" />
                  </td>
                  <td>
                    <input style={{ width: '7rem' }} value={l.unitCost}
                      onChange={e => setLine(i, { unitCost: e.target.value })} placeholder="0.00" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center gap-2 mt-3">
            <button
              className="text-sm text-primary-700 hover:underline"
              onClick={() => setLines(prev => [...prev, { goodsId: '', quantity: '', unitCost: '' }])}
            >
              + eilutė
            </button>
            <button onClick={submit} disabled={busy}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
              {busy ? '…' : 'Įrašyti'}
            </button>
          </div>
        </div>
      )}

      {/* Stock card (2b) — reads both planes. */}
      <div className="card" style={{ padding: '1rem' }}>
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Prekės kortelė</h2>
        <div className="flex items-end gap-2 mb-3">
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">Prekės ID</span>
            <input style={{ width: '6rem' }} value={cardGoodsId} onChange={e => setCardGoodsId(e.target.value)} placeholder="id" />
          </label>
          <button onClick={loadCard} className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400">
            Rodyti
          </button>
        </div>
        {card && (
          <>
            <p className="text-sm text-gray-600 mb-2">{card.goodsName}</p>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Dokumentas</th>
                  <th>Turinys</th>
                  <th style={{ textAlign: 'right' }}>Kiekis</th>
                  <th style={{ textAlign: 'right' }}>Suma</th>
                  <th style={{ textAlign: 'right' }}>Likutis (kiekis)</th>
                  <th style={{ textAlign: 'right' }}>Likutis (vertė)</th>
                </tr>
              </thead>
              <tbody>
                {card.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date.slice(0, 10)}</td>
                    <td>{r.documentNumber ?? ''}</td>
                    <td>{r.content ?? ''}</td>
                    <td style={{ textAlign: 'right' }}>{r.quantityChange}</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={r.totalValue} /></td>
                    <td style={{ textAlign: 'right' }}><strong>{r.runningQuantity}</strong></td>
                    <td style={{ textAlign: 'right' }}><strong><MoneyCell value={r.runningValue} /></strong></td>
                  </tr>
                ))}
                {card.rows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-500">Operacijų nėra</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
